import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { WithFormStyles, withFormStyles } from '../../../util/withFormStyles';
import { submitAnswersForForm } from '../../../store/QuoteWizardState';
import { Form } from '@calefy-inc/informedMaterial';
import { Policy } from '../../../Typescript';
import { useSelector } from 'react-redux';
import { StoreState } from '../../../store';
import { answerInstancesToFormState } from '../QuoteForms/utility';
import RenderedQuestionInstance from '../../common/QuoteComponents/RenderedQuestionInstance';
import { formStateToAnswerInstances } from '../QuoteForms/utility';
import { QuoteWizardForm } from '../classes';
import { useFormApi } from 'informed';
import { FormErrorsWarning } from '../FormErrorsWarning';
import { $TSFixMe } from '@calefy-inc/utilityTypes';
import { getLabel } from './utility';
import { getPolicyStreamFormLabelFromInternalName } from '../../PolicyStream/PolicyStreamReviewPage/utility';

type SetFormErrorPresent = (b: boolean) => void;

const getFormForEdit = (
  policy: Policy | null,
  internalName?: QuoteWizardForm['internalName'],
): ((store: StoreState) => QuoteWizardForm | null) => {
  return (store: StoreState) => {
    if (internalName) {
      const policyForm = store.quoteWizard.policyForms.find(
        (form) => form.internalName === internalName,
      );
      if (policyForm) {
        return policyForm;
      }

      const answeredForm = Object.values(store.quoteWizard.formAnswers).find(
        (formWithAnswers) => formWithAnswers.form.internalName === internalName,
      );
      return answeredForm?.form ?? null;
    }

    if (!policy) {
      const { businessForm } = store.quoteWizard;
      return QuoteWizardForm.isQuoteWizardForm(businessForm)
        ? businessForm
        : null;
    }

    return (
      store.quoteWizard.policyForms.find((form) =>
        form.matchByPolicy(policy),
      ) || null
    );
  };
};

const getInitialValuesForEdit = (
  policy: Policy | null,
  internalName?: QuoteWizardForm['internalName'],
) => {
  return (store: StoreState) => {
    const matching = Object.values(store.quoteWizard.formAnswers).find(
      (answeredForm) => {
        if (internalName) {
          return answeredForm.form.internalName === internalName;
        }

        if (answeredForm.form.internalName) {
          return false;
        }

        return answeredForm.form.matchByPolicy(policy);
      },
    );
    const initialValues = matching
      ? answerInstancesToFormState(matching.answers)
      : undefined;
    return initialValues;
  };
};

const generateGetMatchingAnswers = (form: QuoteWizardForm) => {
  return (state: StoreState) => {
    return state.quoteWizard.formAnswers[form.id];
  };
};

interface FormEditButtonProps {
  policy: Policy | null;
  internalName?: QuoteWizardForm['internalName'];
  setFormErrorPresent: SetFormErrorPresent;
}
export const FormEditButton = ({
  policy,
  internalName,
  setFormErrorPresent,
}: FormEditButtonProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const labelForm = useSelector((store: StoreState) => {
    const form = getFormForEdit(policy, internalName)(store);
    return form ? { form } : null;
  });
  const label = `Edit ${getEditFormLabel(
    labelForm?.form,
    policy,
    internalName,
  )} Form`;

  return (
    <>
      <Tooltip
        title={label}
        enterDelay={100}
        leaveDelay={100}
        placement='right'
      >
        <IconButton
          aria-label={label}
          onClick={() => {
            setOpen(true);
          }}
        >
          <EditIcon fontSize='small' />
        </IconButton>
      </Tooltip>
      <EditFormDialog
        open={open}
        onClose={() => setOpen(false)}
        policy={policy}
        internalName={internalName}
        setFormErrorPresent={setFormErrorPresent}
      />
    </>
  );
};

interface EditFormDialogProps extends WithFormStyles {
  open: boolean;
  onClose: () => void;
  policy: FormEditButtonProps['policy'];
  internalName?: FormEditButtonProps['internalName'];
  setFormErrorPresent: SetFormErrorPresent;
}
const EditFormDialog = withFormStyles()(
  ({
    open,
    onClose,
    classes,
    policy,
    internalName,
    setFormErrorPresent,
  }: EditFormDialogProps) => {
    const form = useSelector(getFormForEdit(policy, internalName));
    const initialValues = useSelector(
      getInitialValuesForEdit(policy, internalName),
    );
    const dispatch = useDispatch();
    const dialogTitle = getEditFormLabel(form, policy, internalName);

    const handleClose = () => {
      setFormErrorPresent(false);
      onClose();
    };

    if (!form) {
      return null;
    }

    return (
      <Dialog open={open} onClose={handleClose} keepMounted={true}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <Form
          className={classes.dialogForm}
          initialValues={initialValues}
          onSubmit={(formState) => {
            const { values } = formState;
            dispatch(
              submitAnswersForForm({
                form,
                answers: formStateToAnswerInstances(
                  values,
                  form.questionInstances,
                ),
              }),
            );
            handleClose();
          }}
        >
          {({ formApi }: $TSFixMe) => (
            <>
              <FormContents
                form={form}
                classes={classes}
                open={open}
                setFormErrorPresent={setFormErrorPresent}
              />
              <FormErrorsWarning />
              <DialogActions>
                <Button
                  onClick={() => {
                    formApi.reset(initialValues);

                    // Promise.resolve() here is ensuring that this code only executes after the form has been reset
                    // this is because formApi.reset, while being a synchronous function, will only update the form
                    // state on the next tick. Promise.resolve() schedules its .then() callback to run after this next tick
                    Promise.resolve().then(() => {
                      const { values } = formApi.getFormState();

                      // touching the fields so that validation messages show up (mostly required fields for our purposes)
                      Object.keys(values).forEach((k) => {
                        formApi.setTouched(k, true);
                      });

                      formApi.validate();
                      handleClose();
                    });
                  }}
                >
                  Cancel
                </Button>
                <Button type='submit'>Submit</Button>
              </DialogActions>
            </>
          )}
        </Form>
      </Dialog>
    );
  },
);

interface FormContentsProps extends WithFormStyles {
  form: QuoteWizardForm;
  open: boolean;
  setFormErrorPresent: SetFormErrorPresent;
}
const FormContents = ({
  form,
  classes,
  open,
  setFormErrorPresent,
}: FormContentsProps) => {
  // const formState = useFormState();
  const formApi = useFormApi();
  // use inline function to avoid unknown dependencies warning
  const getMatchingAnswers = useCallback(generateGetMatchingAnswers(form), [
    form,
  ]);
  const answers = useSelector(getMatchingAnswers);

  useEffect(() => {
    if (!open || !form || !formApi) {
      return;
    }

    formApi.validate();
    const state = formApi.getFormState();
    const { errors } = state;
    setFormErrorPresent(Object.values(errors).length > 0);
    // NB we also want to revalidate these when the answers change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form, formApi, answers]);

  return (
    <DialogContent sx={{ minWidth: '600px' }}>
      {form.questionInstances.map((questionInstance) => (
        <RenderedQuestionInstance
          questionInstance={questionInstance}
          classes={classes}
          key={questionInstance.id}
        />
      ))}
    </DialogContent>
  );
};

const getEditFormLabel = (
  form: QuoteWizardForm | null | undefined,
  policy: Policy | null,
  internalName?: QuoteWizardForm['internalName'],
): string => {
  const resolvedInternalName = internalName ?? form?.internalName;

  if (resolvedInternalName) {
    return getPolicyStreamFormLabelFromInternalName(resolvedInternalName);
  }

  if (form) {
    return getLabel({ form });
  }

  if (policy) {
    return policy.displayName;
  }

  return 'General Information';
};
